import { TranslationKey } from '@Localization';
import { QuestionType } from '../../__legacy__/modules/annotation/question.model';
import { AnnotationToolType } from '../../__legacy__/modules/annotation/submodules/annotationTypes/models/annotationToolType';
import { IJobDatasetDto } from './jobs.api.model';

export enum JOB_TYPE {
  UNKNOWN = 'UNKNOWN',
  TRAINING = 'TRAINING',
  INFERENCE = 'INFERENCE',
}

export type IValidJobType = Exclude<JOB_TYPE, JOB_TYPE.UNKNOWN>;

export enum JOB_STATUS {
  UNKNOWN = 'Unknown', // Used when we didn't get data from backend yet
  DRAFT = 'Draft',
  EVALUATING = 'Evaluating',
  FAILED = 'Failed',
  FAILING = 'Failing',
  FINISHING = 'Finishing',
  FINISHED = 'Finished',
  IMPORTED = 'Imported',
  IMPORTING = 'Importing',
  RETRYING = 'Retrying',
  RUNNING = 'Running',
  SAVING = 'Saving',
  STARTING = 'Starting',
  STOPPED = 'Stopped',
  STOPPING = 'Stopping',
  WAITING = 'Waiting',
}

export const PROGRESS_JOB_STATUSES = [
  JOB_STATUS.STARTING,
  JOB_STATUS.RETRYING,
  JOB_STATUS.RUNNING,
  JOB_STATUS.EVALUATING,
  JOB_STATUS.STOPPING,
  JOB_STATUS.FAILING,
  JOB_STATUS.IMPORTING,
  JOB_STATUS.SAVING,
  JOB_STATUS.FINISHING,
  JOB_STATUS.WAITING,
];

export const DONE_JOB_STATUSES = [JOB_STATUS.IMPORTED, JOB_STATUS.FINISHED, JOB_STATUS.STOPPED, JOB_STATUS.FAILED];

export type IJobStartingProgress = '1/4' | '2/4' | '3/4' | null | undefined;

export enum TRAINING_VARIANT {
  ANOMALY_DETECTION_4 = 'anomaly_detection.detect_anomalies_4',
  ANOMALY_DETECTION_7 = 'anomaly_detection.detect_anomalies_7',
  ANOMALY_DETECTION_8 = 'anomaly_detection.detect_anomalies_8',
  FEATURE_DETECTION = 'feature_detection.detect_feature_1',
  LOCATE_OBJECTS = 'object_location.locate_objects_1',
  LOCATE_POINTS = 'point_location.locate_points_1',
  OBJECT_CLASSIFICATION = 'object_classification.classify_object_1',
}
export const TRAINING_VARIANTS_WITHOUT_RESULTS = [TRAINING_VARIANT.FEATURE_DETECTION];
export const TRAINING_VARIANTS_WITH_MASK = [TRAINING_VARIANT.FEATURE_DETECTION];
export const TRAINING_VARIANTS_WITHOUT_THRESHOLD = [TRAINING_VARIANT.FEATURE_DETECTION, TRAINING_VARIANT.OBJECT_CLASSIFICATION];
export const TRAINING_VARIANTS_WITH_HEATMAP = [
  TRAINING_VARIANT.ANOMALY_DETECTION_4,
  TRAINING_VARIANT.ANOMALY_DETECTION_7,
  TRAINING_VARIANT.ANOMALY_DETECTION_8,
  TRAINING_VARIANT.FEATURE_DETECTION,
];
const OMMITED_TRAINING_VARIANTS_LIST = [TRAINING_VARIANT.ANOMALY_DETECTION_4, TRAINING_VARIANT.ANOMALY_DETECTION_7];
export const TRAINING_VARIANTS_LIST = Object.values(TRAINING_VARIANT).filter((item) => !OMMITED_TRAINING_VARIANTS_LIST.includes(item));

export enum INFERENCE_TYPE {
  PROJECT = 'project',
  DATASET = 'dataset',
}

export enum TRAINING_TYPE {
  MANUAL = 'manual',
  AUTO = 'auto',
}

export enum JOB_RESOURCE_TYPE {
  MASK = 'mask',
  HEATMAP = 'heatmap',
}

interface IJobListItemBase {
  id: string;
  trainingName: string;
  trainingVariant: TRAINING_VARIANT;
  projectName: string;
  date: string;
  score: number | null;
  startingProgress: IJobStartingProgress;
  status: JOB_STATUS;
  failureReason: string | null;
  costCalculated: number | null;
  isOwner: boolean;
  jobType: JOB_TYPE;
  // Used only for sorting
  dateCreated?: never;
  modelName?: never;
  modelVariant?: never;
}

export interface IInferenceListItem extends IJobListItemBase {
  jobType: JOB_TYPE.INFERENCE;
  isTrainingOwner: boolean;
  trainingId: string;
  inferenceName: string;
}

export interface ITrainingListItem extends IJobListItemBase {
  jobType: JOB_TYPE.TRAINING;
  progress: string;
  trainingTime: number;
  trainingType: TRAINING_TYPE;
}

export enum JOB_OWNERSHIP {
  All = 10,
  My = 20,
  Others = 30,
}

export enum JOB_ANNOTATION_TYPE {
  MARKING_TOOL = 'marking_tool',
  QUESTION = 'question',
}

interface IJobAnnotationToolBase {
  id: string;
  name: string;
  tool: JOB_ANNOTATION_TYPE;
  compatible: boolean;
  required: boolean;
}

interface IJobMarkingTool extends IJobAnnotationToolBase {
  type: AnnotationToolType;
  tool: JOB_ANNOTATION_TYPE.MARKING_TOOL;
}

interface IJobQuestionTool extends IJobAnnotationToolBase {
  type: QuestionType;
  tool: JOB_ANNOTATION_TYPE.QUESTION;
}

export type IJobAnnotationTool = IJobMarkingTool | IJobQuestionTool;

interface ISubTrainingJobParam {
  name: string;
  value: string;
}

export interface ISubTrainingJob {
  id: string;
  dateCreated: string;
  name: string;
  progress: string;
  score: number | null;
  status: JOB_STATUS;
  trainingTime: number;
  isBest: boolean;
  inferenceTime: number;
  params: ISubTrainingJobParam[];
}

/**
 * No key set -> not validated yet,
 * null -> valid,
 * object -> invalid
 */
export type IStringError = Map<string, { message: TranslationKey; messageParameters?: Record<string, string | number> } | null>;
/**
 * undefined -> not validated yet,
 * true/false -> invalid/valid
 */
export type IBooleanError = boolean | undefined;
export type IJobListItem = IInferenceListItem | ITrainingListItem;
export type IJobDataset = IJobDatasetDto & {
  isSelected?: boolean;
  testImagesCount?: number;
  trainImagesCount?: number;
  validationImagesCount?: number;
};
export interface IJobProject {
  value: string;
  label: string;
  modelVariant?: TRAINING_VARIANT;
  sizeInMB: number;
}

export enum JOB_DOWNLOAD_TYPE {
  MODEL,
  RESULTS,
}
export interface IJobDownload {
  downloadType: JOB_DOWNLOAD_TYPE;
  urlGenerator: () => Promise<string | null>;
  baseThresholdRange: [number, number];
  currentThresholdRange: [number, number];
}
